/**
 * Transform functions: backend API responses -> frontend types.
 *
 * Backend uses snake_case matching the Klyra data model.
 * Frontend uses camelCase with richer UI types.
 *
 * Data model alignment:
 *   klyra_schema_fields:    field_id, field_name, field_category, definition, data_type, max_length
 *   klyra_known_mappings:   mapping_id, source_field, target_field, schema_id, confidence
 *   klyra_files:            file_id, file_name, uploaded_by, provider_id, schema_id, status,
 *                           storage_path, file_size, mime_type, checksum
 *   klyra_document_mappings: source_field, target_field, confidence
 */

import type {
  SchemaField,
  MappingRow,
  MappingStatus,
  KnownMapping,
  HistoricalFile,
  UploadConfirmation,
  FieldRequirement,
  VolumePathConfig,
} from "@/types";

// ── Backend response shapes ─────────────────────────────────

export interface ApiVolumePathConfig {
  volume_base_path?: string;
  manifest_output_path?: string;
  extraction_output_path?: string;
  source_path?: string;
  kodex_databricks_volume_path?: string;
  kodex_extraction_volume_base_path?: string;
  kodex_extraction_manifest_base_dir?: string;
  kodex_extraction_output_base_dir?: string;
  kodex_extraction_workflow_job_id?: string;
  workflow_extraction_id?: string;
  workflow_run_id?: number | null;
}

export interface ApiUploadResponse {
  documentId: string;
  fileName: string;
  uploadedBy: string;
  uploadedAt: string;
  completedAt?: string;
  targetSchema: string;
  status: "success" | "error";
  storagePath?: string;
  fileSize?: number;
  mimeType?: string;
  checksum?: string;
  message?: string;
  volume_path_config?: ApiVolumePathConfig | null;
}

export function transformVolumePathConfig(
  raw: ApiVolumePathConfig | null | undefined
): VolumePathConfig | null {
  if (!raw) return null;
  return {
    volumeBasePath: raw.volume_base_path ?? "",
    manifestOutputPath: raw.manifest_output_path ?? "",
    extractionOutputPath: raw.extraction_output_path ?? "",
    sourcePath: raw.source_path ?? "",
    kodexDatabricksVolumePath: raw.kodex_databricks_volume_path ?? "",
    kodexExtractionVolumeBasePath: raw.kodex_extraction_volume_base_path ?? "",
    kodexExtractionManifestBaseDir: raw.kodex_extraction_manifest_base_dir ?? "",
    kodexExtractionOutputBaseDir: raw.kodex_extraction_output_base_dir ?? "",
    kodexExtractionWorkflowJobId: raw.kodex_extraction_workflow_job_id ?? "",
    workflowExtractionId: raw.workflow_extraction_id ?? "",
    workflowRunId: raw.workflow_run_id ?? null,
  };
}

export interface ApiMappingResult {
  source_field: string;
  target_field: string | null;
  field_id: string | null;
  field_category: string | null;
  mapping_method: "Green" | "Amber" | "Red" | "Unknown";
  confidence: number;
  top_similarities: string[] | null;
  dataset_name: string | null;
}

export interface ApiSchemaField {
  schema_id: string | null;
  field_id: string;
  field_name: string;
  field_category: string | null;
  data_type: string | null;
  max_length: number | null;
  definition: string | null;
  is_mandatory: boolean;
  is_conditional: boolean;
  condition_description: string | null;
}

export interface ApiKnownMapping {
  mapping_id: string;
  schema_id: string | null;
  provider_id: string | null;
  schema_name: string | null;
  source_field: string | null;
  target_field: string | null;
  confidence: number | null;
  approved_by: string | null;
  created_at: string | null;
  updated_at: string | null;
  // Joined from klyra_schema_fields
  field_name: string | null;
  field_category: string | null;
}

/** Wire shape of a single klyra_field_requirements row from GET /api/v1/field-requirements */
export interface ApiFieldRequirement {
  schema_id: string | null;
  field_id: string | null;
  report_type: string | null;
  is_mandatory: boolean;
  is_conditional: boolean;
  condition_description: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface ApiFile {
  file_id: string;
  uploaded_by: string | null;
  provider_id: string | null;
  schema_id: string | null;
  file_name: string | null;
  status: string | null;
  quality_status: string | null;
  storage_path: string | null;
  file_size: number | null;
  mime_type: string | null;
  checksum: string | null;
  approved_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  latest_dq_run_at?: string | null;
  dq_error_count?: number | null;
  dq_warning_count?: number | null;
  dq_cycle_count?: number | null;
}

export interface ApiDatasetInfo {
  name: string;
  row_count: number;
  column_count: number;
  columns: string[];
  sample_rows: Record<string, unknown>[];
}

export interface ApiProcessResponse {
  file_id: string;
  file_name: string;
  datasets: ApiDatasetInfo[];
  file_metadata: { header_text: string; metadata_fields: Record<string, string> };
  mappings: ApiMappingResult[];
  processing_time_ms: number;
}

// ── Transforms ──────────────────────────────────────────────

export function transformUploadResponse(raw: ApiUploadResponse): UploadConfirmation {
  return {
    documentId: raw.documentId,
    fileName: raw.fileName,
    uploadedBy: raw.uploadedBy,
    uploadedAt: raw.uploadedAt,
    completedAt: raw.completedAt,
    targetSchema: raw.targetSchema,
    status: raw.status,
    storagePath: raw.storagePath,
    fileSize: raw.fileSize,
    mimeType: raw.mimeType,
    checksum: raw.checksum,
    message: raw.message,
    volumePathConfig: transformVolumePathConfig(raw.volume_path_config),
  };
}

export function transformSchemaFields(cols: ApiSchemaField[]): SchemaField[] {
  // Deduplicate by field_id. The API should already aggregate across
  // report_types, but this guard prevents double-counting if duplicate
  // rows slip through from any adapter.
  const seen = new Set<string>();
  return cols
    .filter((c) => {
      if (seen.has(c.field_id)) return false;
      seen.add(c.field_id);
      return true;
    })
    .map((c) => ({
      id: c.field_id,
      fieldName: c.field_name,
      definition: c.definition ?? "",
      fieldCategory: c.field_category ?? "Risk",
      mandatory: c.is_mandatory,
      dataType: c.data_type ?? "string",
      maxLength: c.max_length,
      schemaId: c.schema_id ?? undefined,
    }));
}

/** @deprecated Use transformSchemaFields -- kept for backward compat during migration */
export const transformTargetColumns = transformSchemaFields;

function mapMethodToStatus(method: string): MappingStatus {
  switch (method) {
    case "Green":
      return "known_mapping";
    case "Amber":
      return "ML";
    case "Red":
      return "LLM";
    default:
      return "needs_match";
  }
}

let _counter = 0;
function makeId() {
  return `map-${++_counter}`;
}

export function transformProcessResponse(
  response: ApiProcessResponse,
  schemaFields: SchemaField[]
): { rows: MappingRow[]; sourceColumns: string[]; duplicateColumns: string[] } {
  _counter = 0;

  const fieldByName = new Map(schemaFields.map((f) => [f.fieldName, f]));
  const mandatoryFieldIds = new Set(schemaFields.filter((f) => f.mandatory).map((f) => f.id));

  const mappedMandatoryIds = new Set<string>();
  const sourceColumns = new Set<string>();
  const rows: MappingRow[] = [];

  // Deduplicate by targetFieldId — keep only the highest confidence match per target.
  // Multiple source columns may map to the same target; only show the best one.
  const bestByTarget = new Map<string, (typeof response.mappings)[0]>();
  // UNKNOWN mappings (no target resolved) are collected separately — nothing to deduplicate on.
  const unmatchedMappings: (typeof response.mappings)[0][] = [];

  for (const m of response.mappings) {
    const targetField = m.target_field ? fieldByName.get(m.target_field) : null;
    const targetFieldId = targetField?.id ?? m.field_id ?? "";
    if (!targetFieldId) {
      unmatchedMappings.push(m);
      continue;
    }
    const existing = bestByTarget.get(targetFieldId);
    if (!existing || (m.confidence ?? 0) > (existing.confidence ?? 0)) {
      if (existing) {
        // The displaced mapping still represents a real source column — surface it
        // in the additional fields tab so the user can map it manually.
        unmatchedMappings.push(existing);
      }
      bestByTarget.set(targetFieldId, m);
    } else {
      // Lost the confidence competition for this target — still surface the source column.
      unmatchedMappings.push(m);
    }
  }

  // Track all source columns (including duplicates) for the source dropdown
  for (const m of response.mappings) {
    sourceColumns.add(m.source_field);
  }

  for (const m of bestByTarget.values()) {
    const targetField = m.target_field ? fieldByName.get(m.target_field) : null;
    const targetFieldId = targetField?.id ?? m.field_id ?? "";
    const status = mapMethodToStatus(m.mapping_method);

    // Build suggestion IDs from top_similarities names -> field IDs
    const suggestions: string[] = [];
    if (m.top_similarities) {
      for (const name of m.top_similarities) {
        const f = fieldByName.get(name);
        if (f) suggestions.push(f.id);
      }
    }
    if (targetFieldId && !suggestions.includes(targetFieldId)) {
      suggestions.unshift(targetFieldId);
    }

    if (targetFieldId && mandatoryFieldIds.has(targetFieldId)) {
      mappedMandatoryIds.add(targetFieldId);
    }

    rows.push({
      id: makeId(),
      sourceColumn: m.source_field,
      targetFieldId,
      direction: "source_to_target",
      status,
      confidence: m.confidence,
      approved: m.mapping_method === "Green",
      suggestions,
    });
  }

  // Source columns the pipeline couldn't match to any target — surface them in
  // the additional fields tab as "needs_match" rows so users can map them manually.
  for (const m of unmatchedMappings) {
    const suggestions: string[] = [];
    if (m.top_similarities) {
      for (const name of m.top_similarities) {
        const f = fieldByName.get(name);
        if (f) suggestions.push(f.id);
      }
    }
    rows.push({
      id: makeId(),
      sourceColumn: m.source_field,
      targetFieldId: "",
      direction: "source_to_target",
      status: "needs_match",
      confidence: m.confidence,
      approved: false,
      suggestions,
    });
  }

  // Add target_to_source rows for unmapped mandatory fields
  const allSourceCols = Array.from(sourceColumns);
  for (const f of schemaFields) {
    if (f.mandatory && !mappedMandatoryIds.has(f.id)) {
      rows.push({
        id: makeId(),
        sourceColumn: "",
        targetFieldId: f.id,
        direction: "target_to_source",
        status: "needs_match",
        confidence: 0,
        approved: false,
        suggestions: allSourceCols.slice(0, 5),
      });
    }
  }

  // Detect duplicate column names within each raw dataset header list.
  // Repeated headers across separate datasets/regions are expected and should
  // not be surfaced as duplicate-column warnings.
  const allRawColumns = response.datasets.flatMap((d) => {
    const counts = new Map<string, number>();
    for (const col of d.columns) {
      counts.set(col, (counts.get(col) ?? 0) + 1);
    }
    return Array.from(counts.entries())
      .filter(([, count]) => count > 1)
      .map(([col]) => col);
  });
  const colCounts = new Map<string, number>();
  for (const col of allRawColumns) {
    colCounts.set(col, (colCounts.get(col) ?? 0) + 1);
  }
  const duplicateColumns = Array.from(colCounts.keys());

  return { rows, sourceColumns: allSourceCols, duplicateColumns };
}

export function transformKnownMappings(data: ApiKnownMapping[]): KnownMapping[] {
  return data.map((m) => ({
    mappingId: m.mapping_id,
    sourceField: m.source_field ?? "",
    targetField: m.field_name ?? m.target_field ?? "",
    targetFieldId: m.target_field ?? "",
    schemaId: m.schema_id ?? "",
    schemaName: m.schema_name ?? m.schema_id ?? "",
    providerId: m.provider_id ?? null,
    confidence: m.confidence ?? undefined,
    approvedBy: m.approved_by ?? undefined,
    createdAt: m.created_at ?? undefined,
    updatedAt: m.updated_at ?? undefined,
  }));
}

/**
 * Maps raw API field requirement rows (snake_case) to camelCase FieldRequirement objects.
 * Rows with null schema_id, field_id, or report_type are skipped — they cannot
 * be used for the (schema_id, field_id, report_type) join and would pollute lookups.
 */
export function transformFieldRequirements(data: ApiFieldRequirement[]): FieldRequirement[] {
  return data
    .filter((r) => r.schema_id && r.field_id && r.report_type)
    .map((r) => ({
      schemaId: r.schema_id!,
      fieldId: r.field_id!,
      reportType: r.report_type!,
      isMandatory: r.is_mandatory,
      isConditional: r.is_conditional,
      conditionDescription: r.condition_description ?? undefined,
    }));
}

export function transformFiles(data: ApiFile[]): HistoricalFile[] {
  return data.map((d) => ({
    fileId: d.file_id,
    fileName: d.file_name ?? d.file_id,
    uploadedBy: d.uploaded_by ?? "",
    providerId: d.provider_id ?? "",
    schemaId: d.schema_id ?? "",
    status: d.status ?? "unknown",
    qualityStatus: d.quality_status ?? "",
    storagePath: d.storage_path ?? undefined,
    fileSize: d.file_size ?? undefined,
    mimeType: d.mime_type ?? undefined,
    checksum: d.checksum ?? undefined,
    createdAt: d.created_at ?? "",
    updatedAt: d.updated_at ?? "",
    latestDqRunAt: d.latest_dq_run_at ?? null,
    dqErrorCount: d.dq_error_count ?? null,
    dqWarningCount: d.dq_warning_count ?? null,
    dqCycleCount: d.dq_cycle_count ?? null,
  }));
}

/** @deprecated Use transformFiles -- kept for backward compat */
export const transformDocuments = transformFiles;
